import { useCallback, useEffect, useState } from "react";
import useDebouncedSearch from "../../../hooks/useDebouncedSearch";
import { WEBINAR_PAGE_SIZE_DEFAULT } from "../constants";
import { SessionStatus } from "../types/models";

/**
 * List filter + pagination state.
 * Status / q / page / limit are sent to GET /posts (search is debounced).
 * Tab switch clears search; search needs at least 3 characters.
 */
export function useWebinarFilters() {
  const [status, setStatusState] = useState<"all" | SessionStatus>("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimitState] = useState(WEBINAR_PAGE_SIZE_DEFAULT);

  // Empty clears immediately; 3+ chars hit the API after 400ms.
  const debouncedQ = useDebouncedSearch(q, { minLength: 3, delay: 400 });

  const setStatus = useCallback((next: "all" | SessionStatus) => {
    setStatusState(next);
    setQ("");
    setPage(1);
  }, []);

  const setLimit = useCallback((next: number) => {
    setLimitState(next);
    setPage(1);
  }, []);

  // New search term → back to first page.
  useEffect(() => {
    setPage(1);
  }, [debouncedQ]);

  return {
    status,
    setStatus,
    q,
    setQ,
    debouncedQ,
    page,
    setPage,
    limit,
    setLimit,
  };
}
